import { createLiniyaApi, MemoryTokenStore } from '@liniya/api-client';
import type { MediaStreamLike, PeerConnectionLike } from '@liniya/client-core';
import {
  provideApi,
  provideAppClosing,
  provideKeyValueStore,
  provideReleasePreview,
  provideVisibility,
  provideWebRtc,
} from '@liniya/client-core';

/**
 * The one configured API instance for the browser client.
 *
 * Components never construct their own: a second instance would carry its own
 * token state, and two copies racing to refresh the same chain is exactly the
 * pattern the server treats as a stolen-token replay.
 *
 * The token store is deliberately in memory. The refresh credential lives in
 * an HttpOnly cookie the page cannot read, so nothing durable is exposed to
 * scripts; a reload costs one refresh request and gets a new access token.
 */
const tokenStore = new MemoryTokenStore('cookie');

let authenticationLostHandler: (() => void) | null = null;

export const api = createLiniyaApi({
  baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
  tokenStore,
  onAuthenticationLost: () => authenticationLostHandler?.(),
});

/**
 * Hand the shared layer what only this client can build.
 *
 * At module scope, not in a component: the hooks in `@liniya/client-core` are
 * used by the first thing that renders, and a provider configured during that
 * render would be configured too late.
 */
provideApi(api, onAuthenticationLost);

provideKeyValueStore({
  get: (key) => {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      // Private browsing modes refuse writes; the preference simply does not persist.
    }
  },
});

// A conversation open in a background tab has not been read by anyone.
provideVisibility(() => !document.hidden);

/**
 * The browser's own WebRTC. React Native has none and supplies its own; only
 * these two entry points differ between the clients.
 */
provideWebRtc({
  // The casts cross a deliberate boundary: the shared package describes the
  // handful of members a call uses so that it needs no DOM types at all, and
  // the browser's own classes satisfy that description with far more besides.
  createPeerConnection: (configuration) =>
    new RTCPeerConnection(configuration as RTCConfiguration) as unknown as PeerConnectionLike,
  getUserMedia: async (constraints) =>
    (await navigator.mediaDevices.getUserMedia(
      constraints as MediaStreamConstraints,
    )) as unknown as MediaStreamLike,
  isPermissionDenied: (error) =>
    error instanceof DOMException && error.name === 'NotAllowedError',
});

// A tab closing must not leave the other side of a call ringing.
provideAppClosing((handler) => {
  globalThis.addEventListener('pagehide', handler);
  return () => globalThis.removeEventListener('pagehide', handler);
});

provideReleasePreview((previewUrl) => URL.revokeObjectURL(previewUrl));

/**
 * Register what should happen when the session becomes unrecoverable.
 *
 * The API layer knows the session is gone but must not know how this
 * application navigates, so routing stays on the React side of the boundary.
 */
export function onAuthenticationLost(handler: () => void): () => void {
  authenticationLostHandler = handler;
  return () => {
    if (authenticationLostHandler === handler) authenticationLostHandler = null;
  };
}
